import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Modal, Radio } from 'antd';
import { MacCommandOutlined, PlusCircleFilled } from '@ant-design/icons';
import cx from 'classnames';
import { NotificationType, PackageType } from '../../interfaces';
import { IPackageListPaginated, IPackagesList, IPostPackage, ITariffStatistics } from '../../interfaces';
import { useProfile, useProfileIsRight, useRouterStore, useUiIsSpinnerFound } from '../../hooks';
import {
    addNotification,
    apiHandleErrorMessages,
    createTariffPackage,
    getCompaniesChildrenSelect,
    getCountriesSelect,
    getOperatorsSelect,
    getTariffPackagesStatistics,
} from '../../middleware';
import { getTariffPackagesList } from '../../middleware';
import { tAdvancedPlural } from '../../instruments';
import { tB, tNM, tP, tT } from '../../translate';
import { Catcher, InfiniteScroll, LoadingApiByIds } from '../../Components';
import { CreatePackageForm } from './Components/PackageStep/CreatePackageForm';
import { TariffItemPackageViewCard } from './Components/TariffItem/TariffItemPackageView';
import Styles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

interface ICreateTariffPackage {
    onBack: () => void;
    fetchData: any;
    getStatistics: () => Promise<void>;
}

const CreateTariffPackage: React.FC<ICreateTariffPackage> = ({
    onBack,
    fetchData,
    getStatistics,
}: ICreateTariffPackage) => {
    const [currentView, setCurrentView] = useState<PackageType>(PackageType.LOCAL);
    const isLord = useProfileIsRight('LORD');

    const handleSavePackage = async (packageData: IPostPackage) => {
        const { isError, resp } = await createTariffPackage(packageData);

        if (isError) {
            apiHandleErrorMessages(resp || {});

            return;
        }

        addNotification({
            type: NotificationType.SUCCESS,
            message: 'Created package successfully.',
            isUsersError: true,
        });

        await fetchData();
        await getStatistics();

        onBack();
    };

    return (
        <div className={TariffsStyles.createTariffPackageTypeSelector}>
            {isLord ? (
                <Radio.Group defaultValue="local" buttonStyle="solid" onChange={(e) => setCurrentView(e.target.value)}>
                    <Radio.Button value="local">{tAdvancedPlural('local_package', 2)}</Radio.Button>
                    <Radio.Button value="roaming">{tAdvancedPlural('roaming_package', 2)}</Radio.Button>
                    <Radio.Button value="extra">{tB('Extra Packages')}</Radio.Button>
                </Radio.Group>
            ) : null}

            <div className={TariffsStyles.packageStep}>
                <CreatePackageForm
                    packageType={isLord ? currentView : PackageType.EXTRA}
                    onBack={onBack}
                    onSave={handleSavePackage}
                />
            </div>
        </div>
    );
};

export const TariffsPackagesList: React.FC = observer(() => {
    const [isOpen, setIsOpen] = useState(false);
    const companyId = useProfile().companyId;
    const [currentView, setCurrentView] = useState<'all' | PackageType>('all');
    const [page, setPage] = useState(1);
    const [packageList, setPackageList] = useState<IPackagesList>([]);
    const [tariffStats, setTariffStats] = useState<ITariffStatistics>();
    const routerStore = useRouterStore();
    const isLord = useProfileIsRight('LORD');
    const isUser = useProfileIsRight('USER');
    const isLoading = useUiIsSpinnerFound(['GET_TARIFF_PACKAGES']);

    const pageSize = 10;

    const roamingPackages = tariffStats?.roaming || 0;
    const localPackages = tariffStats?.local || 0;
    const extraPackages = tariffStats?.extra || 0;

    const totalPackage =
        currentView === 'all'
            ? roamingPackages + localPackages + extraPackages
            : currentView === 'roaming'
            ? roamingPackages
            : currentView === 'local'
            ? localPackages
            : extraPackages;

    const openStepper = () => {
        setIsOpen(true);
    };

    const closeStepper = () => {
        setIsOpen(false);
    };

    const handleModalCancel = () => {
        Modal.confirm({
            title: tT('Close Dialog?'),
            content: tT('Are you sure you want to close this dialog? Any unsaved changes will be lost.'),
            okText: tB('Yes'),
            cancelText: tB('No'),
            onOk() {
                closeStepper();
            },
            wrapClassName: 'modal',
        });
    };

    const goTo = (event: React.MouseEvent<HTMLDivElement>, id: string | undefined): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();

            routerStore.goTo('TariffsPackagesItem', { params: { id } }).then();
        }
    };

    const getStatistics = async () => {
        const statistics = await getTariffPackagesStatistics();

        setTariffStats(statistics);
    };

    const fetchData = async (page: number, reset?: boolean) => {
        let resp: IPackageListPaginated;

        if (currentView === 'all') {
            resp = (await getTariffPackagesList(
                { pageNumber: page, pageSize: pageSize },
                true,
            )) as IPackageListPaginated;
        } else {
            resp = (await getTariffPackagesList(
                { packageType: currentView, pageNumber: page, pageSize: pageSize },
                true,
            )) as IPackageListPaginated;
        }

        if (reset) {
            setPackageList(resp.data);
        } else {
            setPackageList((prevList) => [...prevList, ...resp.data]);
        }
    };

    useEffect(() => {
        setPackageList([]);
        setPage(1);
        fetchData(1);
    }, [currentView]);

    useEffect(() => {
        getOperatorsSelect().then();
        getCountriesSelect().then();
        getStatistics().then();

        if (companyId) {
            getCompaniesChildrenSelect(companyId || '', true).then();
        }
    }, []);

    return (
        <Catcher>
            <div className={cx(Styles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffItemContainer}>
                    <div className={TariffsStyles.tariffItemHeaderContainer}>
                        <div>
                            <div className={TariffsStyles.header}>
                                <MacCommandOutlined className={TariffsStyles.icon} />
                                {tP('Package Library')}
                            </div>
                            <p>{tT('Reusable packages that can be assigned to tariffs and cards')}</p>
                        </div>
                        {isUser ? (
                            <Button type={'primary'} onClick={openStepper}>
                                <PlusCircleFilled /> {tP('New Package')}
                            </Button>
                        ) : null}
                    </div>
                    <Divider />
                    {isLord ? (
                        <div className={TariffsStyles.typeSelector}>
                            <Radio.Group
                                defaultValue="all"
                                buttonStyle="solid"
                                onChange={(e) => setCurrentView(e.target.value)}
                            >
                                <Radio.Button value="all">{`${tB('All')} (Local+Roaming+Extra)`}</Radio.Button>
                                <Radio.Button value="local">{tAdvancedPlural('local_package', 2)}</Radio.Button>
                                <Radio.Button value="roaming">{tAdvancedPlural('roaming_package', 2)}</Radio.Button>
                                <Radio.Button value="extra">{tB('Extra Packages')}</Radio.Button>
                            </Radio.Group>
                        </div>
                    ) : null}
                    <InfiniteScroll
                        pageSize={pageSize}
                        page={page}
                        fetchFunc={fetchData}
                        total={totalPackage}
                        current={packageList.length}
                        handleSetPage={setPage}
                    >
                        <div className={TariffsStyles.infiniteScrollContainer}>
                            <div className={cx(TariffsStyles.grid, TariffsStyles.packageGrid)}>
                                {packageList.map((packageInfo) => (
                                    <div key={packageInfo.id} onClick={(e) => goTo(e, packageInfo.id)}>
                                        <TariffItemPackageViewCard
                                            packageInfo={packageInfo}
                                            key={packageInfo.id}
                                            isClickable={true}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </InfiniteScroll>
                    {packageList && packageList.length === 0 && !isLoading && (
                        <div className={TariffsStyles.notFound}>{tNM('No packages found')}</div>
                    )}
                    <Modal
                        title={tP('New Package')}
                        open={isOpen}
                        onCancel={handleModalCancel}
                        wrapClassName={'modal'}
                        footer={null}
                        destroyOnClose
                        maskClosable={true}
                    >
                        <CreateTariffPackage
                            onBack={closeStepper}
                            fetchData={() => fetchData(1, true)}
                            getStatistics={getStatistics}
                        />
                    </Modal>
                </div>
                <LoadingApiByIds idSpinners={['GET_TARIFF_PACKAGES']} />
            </div>
        </Catcher>
    );
});
